import { generateTestId } from "@client-shared";
import type { FormProduct, FormVariant, ProductConnection } from "@shared/types";
import { REQUIRED_GRIND } from "@bcc/components/shopify-import/constants";
import type { BagSizeGroup } from "@bcc/components/shopify-import/types";

export interface VariantsCountObject {
  totalVariants: number;
  localVariants: number;
  importedRemoteVariants: number;
  rejectedVariants: number;
}

export interface VariantAttributes {
  variantsCountObject: VariantsCountObject;
  isProductEnabled: boolean;
  hasAllVariantsImported: boolean;
}

export const PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS = {
  COMPONENT: "productSelectFormRow",
  DESCRIPTION: {
    HAS_DRAFT: "has-draft",
    DELETE_DRAFT: "delete-draft",
    TOTAL_VARIANTS_TEXT: "total-variants-text",
    TOTAL_VARIANTS_COUNT: "total-variants-count",
    ALL_VARIANTS_IMPORTED: "all-variants-imported",
    NO_VARIANTS_DETECTED: "no-variants-detected",
    IMPORTED_LIVE: "imported-live",
    IMPORTED_PENDING: "imported-pending",
    IMPORTED_REJECTED: "imported-rejected",
    CHECKBOX: "checkbox",
    TITLE: "title",
  },
};

export const getFormRowTestIds = (uuid: string) => {
  return {
    checkboxTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.CHECKBOX,
      uuid
    ),
    titleTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.TITLE,
      uuid
    ),
    noVariantsDetectedTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.NO_VARIANTS_DETECTED,
      uuid
    ),
    hasDraftTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.HAS_DRAFT,
      uuid
    ),
    deleteDraftTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.DELETE_DRAFT,
      uuid
    ),
    totalVariantsTextTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.TOTAL_VARIANTS_TEXT,
      uuid
    ),
    totalVariantsCountTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.TOTAL_VARIANTS_COUNT,
      uuid
    ),
    importedLiveTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.IMPORTED_LIVE,
      uuid
    ),
    importedPendingTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.IMPORTED_PENDING,
      uuid
    ),
    importedRejectedTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.IMPORTED_REJECTED,
      uuid
    ),
    allVariantsImportedTestId: generateTestId(
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.COMPONENT,
      PRODUCT_SELECT_FORM_ROW_TEST_ID_KEYS.DESCRIPTION.ALL_VARIANTS_IMPORTED,
      uuid
    ),
  };
};

const getImportedRemoteVariants = (variants: FormVariant[] | undefined) => {
  return variants?.filter((variant) => variant.isImportedRemote) ?? [];
};

const isLiveVariant = (variant: FormVariant) => Boolean(variant.isImportedLocalVariant);

const isRejectedVariant = (variant: FormVariant) => variant.status === "Rejected" && !isLiveVariant(variant);

const getPendingImportedRemoteVariants = (variants: FormVariant[]) => {
  return variants.filter((variant) => variant.isImportedRemote && !isLiveVariant(variant) && !isRejectedVariant(variant));
};

const getConnectionVariantIds = (connections: ProductConnection[] | undefined) => {
  return connections?.map((connection) => connection?.targetVariantId) ?? [];
};

const getTotalImportedVariantIds = (connectionVariantIds: string[], importedRemoteVariants: FormVariant[]) => {
  return [...new Set([...connectionVariantIds, ...(importedRemoteVariants ?? []).map((variant) => variant.id)])];
};

const getRejectedVariants = (variants: FormVariant[]) => {
  return variants.filter(isRejectedVariant);
};

const getImportedLocalVariants = (variants: FormVariant[]) => {
  return variants.filter((variant) => variant.isImportedLocalVariant);
};

const getHasEmptyVariantsArray = (variants: FormVariant[]) => {
  return variants.length === 0;
};

interface BagSizeAndGrindTypeObject {
  bagSize: string;
  grindType: string | undefined;
}

const getImportedLocalBagSizes = (product: FormProduct) => {
  return product.localVariants?.map((variant) => variant.bagSize);
};

const getImportedRemoteBagSizes = (importedRemoteVariants: FormVariant[]) => {
  return importedRemoteVariants.map((variant) => variant.bagSize);
};

const getBagSizeGroups = (
  variants: FormVariant[],
  importedLocalBagSizes: string[],
  importedRemoteBagSizes: string[],
  totalImportedVariantIds: string[]
): BagSizeGroup[] => {
  return [...new Set([...importedLocalBagSizes, ...importedRemoteBagSizes])].map((bagSize) => ({
    bagSize,
    grindTypes: [
      ...new Set(
        variants
          .filter((variant) => variant.bagSize === bagSize && variant.grind && totalImportedVariantIds.includes(variant.id))
          .flatMap((variant) => variant.grind ?? [])
          .filter((grind): grind is string => grind !== undefined)
      ),
    ],
  }));
};

const getVariantsImportedHasMappedGrind = (variants: FormVariant[]): BagSizeAndGrindTypeObject[] => {
  return variants
    .filter((variant) => variant.isImportedRemote && variant.detectedGrindInfo?.some((grind) => grind.mapDetectedGrindTo))
    .flatMap(
      (variant) =>
        variant.detectedGrindInfo
          ?.filter((grind) => grind.mapDetectedGrindTo)
          .map((grind) => ({
            grindType: grind.mapDetectedGrindTo,
            bagSize: variant.bagSize,
          })) ?? []
    );
};

const getVariantsImportedHasMappedGrindSet = (variantsImportedHasMappedGrind: BagSizeAndGrindTypeObject[]) => {
  return new Set(variantsImportedHasMappedGrind.map((variant) => `${variant.grindType}-${variant.bagSize}`));
};

const getHasRemainingValidVariants = (
  variants: FormVariant[],
  variantsImportedHasMappedGrindSet: Set<string>,
  importedBagSizeGroups: BagSizeGroup[],
  isSingleBagSizeEnabled: boolean
) => {
  return variants
    .filter((variant) => !variant.isImportedRemote)
    .filter((variant) => !variantsImportedHasMappedGrindSet.has(`${variant.grind?.[0]}-${variant.bagSize}`))
    .some((variant) => {
      const hasBagSizeAndGrindInBagSizeGroups = Boolean(
        importedBagSizeGroups.find((group) => group.bagSize === variant.bagSize && group.grindTypes.includes(variant.grind?.[0] as string))
      );

      if (
        variant.hasDetectedBagSize &&
        variant.hasValidDetectedBagSize &&
        !variant.hasDetectedGrindType &&
        !importedBagSizeGroups.find((group) => group.bagSize === variant.bagSize && variant.grind?.includes(REQUIRED_GRIND))
      ) {
        return true;
      }

      // Variants without a valid detected bag size are still importable via
      // the override path as long as their bag size + grind isn't already in
      // an imported group. Previously this branch also required
      // importedBagSizeGroups.length === 0, which incorrectly disabled the
      // whole product row as soon as any sibling variant had been submitted
      // In single-bag-size mode the imported bag size is the only
      // allowed one, so the override fallback is intentionally suppressed
      // there to keep that constraint enforced at the product-row level.
      if (
        !variant.hasValidDetectedBagSize &&
        !hasBagSizeAndGrindInBagSizeGroups &&
        (!isSingleBagSizeEnabled || importedBagSizeGroups.length === 0)
      ) {
        return true;
      }

      if (
        variant.hasValidDetectedBagSize &&
        variant.hasValidDetectedGrindType === undefined &&
        !variant.isImportedRemote &&
        !hasBagSizeAndGrindInBagSizeGroups
      ) {
        return true;
      }

      if (variant.hasValidDetectedBagSize && variant.hasValidDetectedGrindType && !hasBagSizeAndGrindInBagSizeGroups) {
        return true;
      }

      return false;
    });
};

const getHasAllSingleBagSizeImported = ({
  isSingleBagSizeEnabled,
  importedLocal,
  singleImportedBagSize,
  productImportedRemoteVariantIds,
  singleBagSizeAllowedVariants,
}: {
  isSingleBagSizeEnabled?: boolean;
  importedLocal: FormVariant[] | undefined;
  productImportedRemoteVariantIds: string[];
  singleBagSizeAllowedVariants: FormVariant[];
  singleImportedBagSize: string | undefined;
}) => {
  if (!isSingleBagSizeEnabled) {
    return false;
  }

  if (importedLocal) {
    if (!singleImportedBagSize) {
      return false;
    }

    return singleBagSizeAllowedVariants.every((variant) => importedLocal.find((localVariant) => localVariant.id === variant.id));
  }

  return productImportedRemoteVariantIds.length > 0 && productImportedRemoteVariantIds.length === singleBagSizeAllowedVariants.length;
};

const getHasAllVariantsImported = (totalImportedVariantIds: string[], rejectedVariants: FormVariant[], variants: FormVariant[]) => {
  if (variants.length === 0) return false;
  // Only count imported ids that still correspond to a variant on the product.
  // A stale connection whose targetVariantId no longer exists in `variants`
  // would otherwise inflate the count and flip this to true while rejected
  // variants are present, producing a contradictory "all imported" badge.
  const variantIds = new Set(variants.map((variant) => variant.id));
  const validImportedVariantIds = totalImportedVariantIds.filter((id) => variantIds.has(id));
  return validImportedVariantIds.length - rejectedVariants.length === variants.length;
};

const getIsProductEnabled = (
  isSingleBagSizeEnabled: boolean,
  hasAllVariantsImported: boolean,
  hasAllSingleBagSizeImported: boolean,
  hasRejectedVariants: boolean,
  hasRemainingValidVariants: boolean,
  hasEmptyVariantsArray: boolean
) => {
  if (hasEmptyVariantsArray) {
    return false;
  }
  if (hasRejectedVariants) {
    return true;
  }
  if (!hasRemainingValidVariants) {
    return false;
  }
  if (isSingleBagSizeEnabled) {
    return !hasAllSingleBagSizeImported;
  }
  return !hasAllVariantsImported;
};

export const getVariantAttributes = (product: FormProduct, isSingleBagSizeEnabled = false): VariantAttributes => {
  const { variants, connections } = product;

  const importedRemoteVariants = getImportedRemoteVariants(variants);
  const connectionVariantIds = getConnectionVariantIds(connections);
  const totalImportedVariantIds = getTotalImportedVariantIds(connectionVariantIds, importedRemoteVariants);
  const hasEmptyVariantsArray = getHasEmptyVariantsArray(variants);
  const rejectedVariants = getRejectedVariants(variants);
  const importedLocalVariants = getImportedLocalVariants(variants);
  const pendingImportedRemoteVariants = getPendingImportedRemoteVariants(variants);
  const importedLocalBagSizes = getImportedLocalBagSizes(product) ?? [];
  const importedRemoteBagSizes = getImportedRemoteBagSizes(importedRemoteVariants);
  const importedBagSizeGroups = getBagSizeGroups(variants, importedLocalBagSizes, importedRemoteBagSizes, totalImportedVariantIds);
  const variantsImportedHasMappedGrind = getVariantsImportedHasMappedGrind(variants);
  const variantsImportedHasMappedGrindSet = getVariantsImportedHasMappedGrindSet(variantsImportedHasMappedGrind);

  const hasAllVariantsImported = getHasAllVariantsImported(totalImportedVariantIds, rejectedVariants, variants);
  const hasRemainingValidVariants = getHasRemainingValidVariants(
    variants,
    variantsImportedHasMappedGrindSet,
    importedBagSizeGroups,
    isSingleBagSizeEnabled
  );

  let singleImportedBagSize: string | undefined;

  if (isSingleBagSizeEnabled) {
    singleImportedBagSize = importedLocalVariants?.length ? importedLocalBagSizes[0] : importedRemoteBagSizes[0];
  }

  const hasAllSingleBagSizeImported = getHasAllSingleBagSizeImported({
    isSingleBagSizeEnabled,
    importedLocal: importedLocalVariants,
    productImportedRemoteVariantIds: importedRemoteVariants.map((variant) => variant.id),
    singleBagSizeAllowedVariants: product.variants.filter((variant) => variant.bagSize === singleImportedBagSize),
    singleImportedBagSize: importedLocalBagSizes[0],
  });

  const isProductEnabled = getIsProductEnabled(
    isSingleBagSizeEnabled,
    hasAllVariantsImported,
    hasAllSingleBagSizeImported,
    rejectedVariants.length > 0,
    hasRemainingValidVariants,
    hasEmptyVariantsArray
  );

  const variantsCountObject: VariantsCountObject = {
    totalVariants: variants.length,
    localVariants: importedLocalVariants?.length ?? 0,
    importedRemoteVariants: pendingImportedRemoteVariants.length,
    rejectedVariants: rejectedVariants.length,
  };
  return {
    variantsCountObject,
    isProductEnabled,
    hasAllVariantsImported,
  };
};
